import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Bookmark, Calendar, Check, ChevronDown, CircleDot, Hash, Layers, Loader2, Plus, Save, Search, Trash2, Type, X,
} from 'lucide-react';
import DatePicker from './DatePicker';
import {
  CONTROLLED_OPS, OPS, chipClass, defaultOp, newGroup, newRule, ruleComplete,
} from './tableFilterEngine';

// VertexTable "Filtreler" paneli (ClickUp deseni): kayıtlı filtreler · kolon seçimi (aramalı, tür
// ikonlu) · koşul · değer (aramalı çoklu seçim + renkli çip) · iç içe grup · VE/VEYA.
// Durum ve uygulama DataTable'dadır; bu bileşen yalnız düzenler (root + onChange).

const TYPE_ICON = { values: CircleDot, text: Type, range: Hash, daterange: Calendar };

// Panel içindeki açılır listeler panelin overflow'unda kırpılmasın → body'ye portal.
function Pop({ anchor, open, onClose, width = 260, children }) {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  useLayoutEffect(() => {
    if (!open || !anchor) return;
    const b = anchor.getBoundingClientRect();
    const w = Math.min(width, window.innerWidth - 16);
    const left = Math.min(Math.max(8, b.left), window.innerWidth - w - 8);
    const below = window.innerHeight - b.bottom - 12;
    const top = below > 220 ? b.bottom + 4 : Math.max(8, b.top - Math.min(360, b.top - 16) - 4);
    setPos({ left, top, width: w, maxH: below > 220 ? Math.min(360, below) : Math.min(360, b.top - 16) });
  }, [open, anchor, width]);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (ref.current?.contains(e.target) || anchor?.contains(e.target)) return;
      onClose();
    };
    const k = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('mousedown', h, true);
    document.addEventListener('keydown', k, true);
    return () => { document.removeEventListener('mousedown', h, true); document.removeEventListener('keydown', k, true); };
  }, [open, anchor, onClose]);
  if (!open || !pos) return null;
  return createPortal(
    <div ref={ref} data-advflt-root
      style={{ left: pos.left, top: pos.top, width: pos.width, maxHeight: pos.maxH }}
      className="fixed z-[125] bg-white border border-gray-200 rounded-xl shadow-xl flex flex-col overflow-hidden">
      {children}
    </div>,
    document.body,
  );
}

function FieldPicker({ columns, value, onPick, typeOf, usedAccs }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const btn = useRef(null);
  const col = columns.find((c) => c.accessor === value);
  const Icon = col ? (TYPE_ICON[typeOf(col.accessor)] || CircleDot) : null;
  const list = columns.filter((c) => !q || String(c.header || c.accessor).toLocaleLowerCase('tr').includes(q.toLocaleLowerCase('tr')));
  return (
    <>
      <button ref={btn} type="button" onClick={() => setOpen((p) => !p)}
        className={`h-8 w-full min-w-0 inline-flex items-center gap-1.5 px-2 rounded-lg border text-sm text-left ${col ? 'border-gray-200 bg-white text-gray-800' : 'border-dashed border-gray-300 bg-gray-50 text-gray-400'} hover:border-gray-300`}>
        {Icon && <Icon size={13} className="text-gray-400 shrink-0" />}
        <span className="truncate flex-1">{col ? (col.header || col.accessor) : 'Sütun seç'}</span>
        <ChevronDown size={13} className="text-gray-400 shrink-0" />
      </button>
      <Pop anchor={btn.current} open={open} onClose={() => { setOpen(false); setQ(''); }} width={260}>
        <div className="p-2 border-b border-gray-100">
          <div className="flex items-center gap-1.5 h-8 px-2 rounded-lg bg-gray-50 border border-gray-200">
            <Search size={13} className="text-gray-400" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Sütun ara…"
              className="flex-1 bg-transparent text-sm outline-none" />
          </div>
        </div>
        <div className="overflow-y-auto py-1">
          {list.map((c) => {
            const I = TYPE_ICON[typeOf(c.accessor)] || CircleDot;
            const used = usedAccs?.has(c.accessor) && c.accessor !== value;
            return (
              <button key={c.accessor} type="button" disabled={used}
                onClick={() => { onPick(c.accessor); setOpen(false); setQ(''); }}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left ${used ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'} ${c.accessor === value ? 'bg-primary-50 text-primary-700' : ''}`}>
                <I size={14} className="text-gray-400 shrink-0" />
                <span className="truncate flex-1">{c.header || c.accessor}</span>
                {used && <span className="text-[10px] text-gray-400">kullanılıyor</span>}
              </button>
            );
          })}
          {!list.length && <div className="px-3 py-3 text-xs text-gray-400">Sütun bulunamadı</div>}
        </div>
      </Pop>
    </>
  );
}

function OpPicker({ type, value, onPick, controlled }) {
  const allowed = controlled ? CONTROLLED_OPS[type] || [] : null;
  const ops = (OPS[type] || OPS.values).filter((o) => !allowed || allowed.includes(o.k));
  return (
    <div className="relative">
      <select value={value || ''} onChange={(e) => onPick(e.target.value)}
        className="h-8 w-full appearance-none pl-2 pr-7 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20">
        {ops.map((o) => <option key={o.k} value={o.k}>{o.l}</option>)}
      </select>
      <ChevronDown size={13} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
    </div>
  );
}

function ValuePicker({ acc, values, onChange, getOptions }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [state, setState] = useState({ loading: false, items: [] });
  const labels = useRef({});
  const btn = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    let dead = false;
    setState((p) => ({ ...p, loading: true }));
    const t = setTimeout(() => {
      Promise.resolve(getOptions(acc, q))
        .then((arr) => {
          if (dead) return;
          for (const o of arr || []) if (o && o.label != null) labels.current[o.value] = o.label;
          setState({ loading: false, items: arr || [] });
        })
        .catch(() => { if (!dead) setState({ loading: false, items: [] }); });
    }, 180);
    return () => { dead = true; clearTimeout(t); };
  }, [open, q, acc, getOptions]);
  const disp = (v) => (labels.current[v] ?? v) || '(boş)';
  const items = state.items.filter((o) => !q || String(o.label ?? o.value ?? '').toLocaleLowerCase('tr').includes(q.toLocaleLowerCase('tr')));
  const allVals = items.map((o) => o.value);
  const allSel = allVals.length > 0 && allVals.every((v) => values.includes(v));
  const toggle = (v) => onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  return (
    <>
      <button ref={btn} type="button" onClick={() => setOpen((p) => !p)}
        className="min-h-[32px] w-full min-w-0 flex flex-wrap items-center gap-1 px-1.5 py-1 rounded-lg border border-gray-200 bg-white text-left hover:border-gray-300">
        {values.length === 0 && <span className="px-1 text-sm text-gray-400">Değer seç</span>}
        {values.slice(0, 3).map((v) => (
          <span key={v} className={`inline-flex items-center gap-1 max-w-[150px] rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${chipClass(v)}`}>
            <span className="truncate">{disp(v)}</span>
            <span role="button" tabIndex={-1} onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); toggle(v); }} className="opacity-60 hover:opacity-100"><X size={11} /></span>
          </span>
        ))}
        {values.length > 3 && <span className="text-[11px] text-gray-500 px-1">+{values.length - 3}</span>}
        <ChevronDown size={13} className="ml-auto text-gray-400 shrink-0" />
      </button>
      <Pop anchor={btn.current} open={open} onClose={() => { setOpen(false); setQ(''); }} width={300}>
        <div className="p-2 border-b border-gray-100">
          <div className="flex items-center gap-1.5 h-8 px-2 rounded-lg bg-gray-50 border border-gray-200">
            <Search size={13} className="text-gray-400" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara…"
              className="flex-1 bg-transparent text-sm outline-none" />
            {state.loading && <Loader2 size={13} className="animate-spin text-gray-400" />}
          </div>
        </div>
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-50 text-xs">
          <button type="button" disabled={!allVals.length}
            onClick={() => onChange(allSel ? values.filter((v) => !allVals.includes(v)) : Array.from(new Set([...values, ...allVals])))}
            className="text-primary-600 hover:underline disabled:opacity-40">{allSel ? 'Seçimi kaldır' : 'Tümünü seç'}</button>
          {values.length > 0 && (
            <button type="button" onClick={() => onChange([])} className="text-gray-500 hover:text-gray-800">Temizle ({values.length})</button>
          )}
        </div>
        <div className="overflow-y-auto py-1">
          {items.map((o) => {
            const on = values.includes(o.value);
            return (
              <button key={String(o.value)} type="button" onClick={() => toggle(o.value)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50">
                <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? 'bg-primary-500 border-primary-500' : 'border-gray-300 bg-white'}`}>
                  {on && <Check size={11} className="text-white" />}
                </span>
                <span className={`inline-flex max-w-[200px] rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${chipClass(o.value)}`}>
                  <span className="truncate">{(o.label ?? o.value) || '(boş)'}</span>
                </span>
                {o.count != null && <span className="ml-auto text-[11px] text-gray-400 tabular-nums">{o.count}</span>}
              </button>
            );
          })}
          {!state.loading && !items.length && <div className="px-3 py-3 text-xs text-gray-400">Değer yok</div>}
        </div>
      </Pop>
    </>
  );
}

function ValueEditor({ rule, type, onPatch, getOptions }) {
  if (rule.op === 'empty' || rule.op === 'not_empty') return <div className="h-8" />;
  if (!rule.acc) return <div className="h-8 rounded-lg border border-dashed border-gray-200 bg-gray-50" />;
  if (type === 'values') {
    return <ValuePicker acc={rule.acc} values={rule.values || []} onChange={(values) => onPatch({ values })} getOptions={getOptions} />;
  }
  if (type === 'daterange') {
    return (
      <div className="flex items-center gap-1.5">
        <DatePicker value={rule.v || ''} onChange={(v) => onPatch({ v })} presets="past" className="flex-1 min-w-0" />
        {rule.op === 'between' && <>
          <span className="text-xs text-gray-400">–</span>
          <DatePicker value={rule.v2 || ''} onChange={(v2) => onPatch({ v2 })} presets="past" className="flex-1 min-w-0" />
        </>}
      </div>
    );
  }
  const inp = 'h-8 w-full min-w-0 px-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20';
  if (type === 'range') {
    return (
      <div className="flex items-center gap-1.5">
        <input type="number" value={rule.v ?? ''} onChange={(e) => onPatch({ v: e.target.value })} placeholder={rule.op === 'between' ? 'En az' : 'Değer'} className={inp} />
        {rule.op === 'between' && <>
          <span className="text-xs text-gray-400">–</span>
          <input type="number" value={rule.v2 ?? ''} onChange={(e) => onPatch({ v2: e.target.value })} placeholder="En çok" className={inp} />
        </>}
      </div>
    );
  }
  return <input value={rule.v ?? ''} onChange={(e) => onPatch({ v: e.target.value })} placeholder="Metin yazın" className={inp} />;
}

function LogicCell({ idx, logic, onLogic, disabled }) {
  if (idx === 0) return <span className="text-xs font-medium text-gray-500 pl-1">Koşul</span>;
  if (idx === 1 && !disabled) {
    return (
      <div className="relative">
        <select value={logic} onChange={(e) => onLogic(e.target.value)}
          className="h-8 w-full appearance-none pl-2 pr-6 rounded-lg border border-gray-200 bg-white text-xs font-semibold text-gray-600 focus:outline-none">
          <option value="and">ve</option>
          <option value="or">veya</option>
        </select>
        <ChevronDown size={12} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
      </div>
    );
  }
  return <span className="text-xs font-semibold text-gray-500 pl-2">{logic === 'or' ? 'veya' : 've'}</span>;
}

function GroupEditor({ node, onChange, depth, ctx }) {
  const { columns, typeOf, controlled, getOptions } = ctx;
  const usedAccs = useMemo(
    () => (controlled ? new Set(node.rules.filter((r) => !r.group && r.acc).map((r) => r.acc)) : null),
    [controlled, node.rules],
  );
  const setRules = (rules) => onChange({ ...node, rules });
  const patch = (id, p) => setRules(node.rules.map((r) => (r.id === id ? { ...r, ...p } : r)));
  const remove = (id) => setRules(node.rules.filter((r) => r.id !== id));
  return (
    <div className="space-y-2">
      {node.rules.map((r, i) => (
        <div key={r.id} className="grid grid-cols-[56px_minmax(0,1fr)_28px] sm:grid-cols-[64px_minmax(0,1fr)_28px] gap-2 items-start">
          <div className="pt-1.5"><LogicCell idx={i} logic={node.logic} onLogic={(logic) => onChange({ ...node, logic })} disabled={controlled} /></div>
          {r.group ? (
            <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-2.5">
              <GroupEditor node={r} depth={depth + 1} ctx={ctx}
                onChange={(g) => setRules(node.rules.map((x) => (x.id === r.id ? g : x)))} />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_150px_minmax(0,1.4fr)] gap-1.5">
              <FieldPicker columns={columns} value={r.acc} typeOf={typeOf} usedAccs={usedAccs}
                onPick={(acc) => patch(r.id, { acc, op: defaultOp(typeOf(acc)), values: [], v: '', v2: '' })} />
              {r.acc ? (
                <OpPicker type={typeOf(r.acc)} value={r.op} controlled={controlled}
                  onPick={(op) => patch(r.id, { op })} />
              ) : <div className="h-8 rounded-lg border border-dashed border-gray-200 bg-gray-50" />}
              <ValueEditor rule={r} type={typeOf(r.acc)} getOptions={getOptions} onPatch={(p) => patch(r.id, p)} />
            </div>
          )}
          <button type="button" onClick={() => remove(r.id)} title={r.group ? 'Grubu kaldır' : 'Koşulu kaldır'}
            className="h-8 w-7 inline-flex items-center justify-center rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50">
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <div className="flex items-center gap-3 pl-[64px] sm:pl-[72px]">
        <button type="button" onClick={() => setRules([...node.rules, newRule()])}
          className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-primary-700">
          <Plus size={13} /> Koşul ekle
        </button>
        {!controlled && depth === 0 && (
          <button type="button" onClick={() => setRules([...node.rules, newGroup(node.logic === 'or' ? 'and' : 'or')])}
            className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-primary-700">
            <Layers size={13} /> İç içe koşul grubu
          </button>
        )}
      </div>
    </div>
  );
}

function SavedMenu({ saved, onApply, onSave, onDelete, canSave }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const btn = useRef(null);
  return (
    <>
      <button ref={btn} type="button" onClick={() => setOpen((p) => !p)}
        className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-gray-200 bg-white text-xs font-medium text-gray-600 hover:bg-gray-50">
        <Bookmark size={13} /> Kayıtlı filtreler {saved.length > 0 && <span className="text-gray-400">({saved.length})</span>}
        <ChevronDown size={12} className="text-gray-400" />
      </button>
      <Pop anchor={btn.current} open={open} onClose={() => setOpen(false)} width={280}>
        <div className="overflow-y-auto py-1">
          {saved.map((s) => (
            <div key={s.id} className="group flex items-center gap-1 px-1">
              <button type="button" onClick={() => { onApply(s); setOpen(false); }}
                className="flex-1 min-w-0 flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-gray-700 hover:bg-gray-50 text-left">
                <Bookmark size={13} className="text-primary-500 shrink-0" />
                <span className="truncate">{s.name}</span>
              </button>
              <button type="button" onClick={() => onDelete(s.id)} title="Kayıtlı filtreyi sil"
                className="h-7 w-7 inline-flex items-center justify-center rounded-lg text-gray-300 hover:text-red-600 hover:bg-red-50">
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          {!saved.length && <div className="px-3 py-2 text-xs text-gray-400">Henüz kayıtlı filtre yok.</div>}
        </div>
        <div className="border-t border-gray-100 p-2 space-y-1.5">
          <div className="text-[11px] font-medium text-gray-500">Geçerli filtreyi kaydet</div>
          <div className="flex gap-1.5">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ad (ör. Bekleyen IFC)"
              onKeyDown={(e) => { if (e.key === 'Enter' && name.trim() && canSave) { onSave(name.trim()); setName(''); } }}
              className="flex-1 min-w-0 h-8 px-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20" />
            <button type="button" disabled={!name.trim() || !canSave} onClick={() => { onSave(name.trim()); setName(''); }}
              className="h-8 px-2.5 inline-flex items-center gap-1 rounded-lg bg-primary-500 text-white text-xs font-semibold disabled:opacity-40">
              <Save size={12} /> Kaydet
            </button>
          </div>
          {!canSave && <div className="text-[11px] text-gray-400">Kaydetmek için önce en az bir koşul tamamlayın.</div>}
        </div>
      </Pop>
    </>
  );
}

/**
 * Props:
 *  anchor, open, onClose
 *  root {logic, rules}, onChange(root)
 *  columns [{accessor, header}] (filtrelenebilir kolonlar), typeOf(acc)
 *  controlled — sunucu-sayfalı tablo: yalnız huninin ifade ettiği koşullar, VE, grup yok
 *  getOptions(acc, q) → [{value,label,count}] | Promise
 *  saved [{id,name}], onApplySaved(s), onSaveCurrent(name), onDeleteSaved(id), activeCount
 */
export default function DataTableFilterPanel({
  anchor, open, onClose, root, onChange, columns, typeOf, controlled, getOptions,
  saved = [], onApplySaved, onSaveCurrent, onDeleteSaved, activeCount = 0,
}) {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  useLayoutEffect(() => {
    if (!open || !anchor) return;
    const place = () => {
      const b = anchor.getBoundingClientRect();
      const w = Math.min(720, window.innerWidth - 16);
      const left = Math.min(Math.max(8, b.right - w), window.innerWidth - w - 8);
      const top = b.bottom + 6;
      setPos({ left, top, width: w, maxH: Math.max(260, window.innerHeight - top - 16) });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open, anchor]);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      const t = e.target;
      if (ref.current?.contains(t) || anchor?.contains(t)) return;
      // Panelin kendi açılır listeleri ve tarih seçici body'de durur — onlara tıklamak paneli kapatmaz
      if (t.closest?.('[data-advflt-root]') || t.closest?.('[role="dialog"][aria-label="Tarih seç"]')) return;
      onClose();
    };
    const k = (e) => { if (e.key === 'Escape' && !document.querySelector('[data-advflt-root]')) onClose(); };
    document.addEventListener('mousedown', h, true);
    document.addEventListener('keydown', k);
    return () => { document.removeEventListener('mousedown', h, true); document.removeEventListener('keydown', k); };
  }, [open, anchor, onClose]);
  if (!open || !pos) return null;
  const ctx = { columns, typeOf, controlled, getOptions };
  const isEmpty = !root.rules.length;
  return createPortal(
    <div ref={ref} style={{ left: pos.left, top: pos.top, width: pos.width, maxHeight: pos.maxH }}
      className="fixed z-[115] bg-white border border-gray-200 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
        <span className="text-sm font-semibold text-gray-900">Filtreler</span>
        {activeCount > 0 && <span className="px-1.5 py-0.5 rounded-md bg-primary-50 text-primary-700 text-[11px] font-semibold">{activeCount} koşul</span>}
        <div className="ml-auto flex items-center gap-2">
          <SavedMenu saved={saved} onApply={onApplySaved} onSave={onSaveCurrent} onDelete={onDeleteSaved} canSave={activeCount > 0} />
          {!isEmpty && (
            <button type="button" onClick={() => onChange({ ...root, rules: [] })}
              className="h-8 px-2.5 rounded-lg text-xs font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-50">Temizle</button>
          )}
          <button type="button" onClick={onClose} aria-label="Kapat" className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-50">
            <X size={15} />
          </button>
        </div>
      </div>
      <div className="overflow-y-auto px-4 py-3">
        {isEmpty ? (
          <div className="py-6 text-center">
            <p className="text-sm text-gray-500 mb-3">Henüz koşul yok. Bir sütun seçerek başlayın.</p>
            <button type="button" onClick={() => onChange({ ...root, rules: [newRule()] })}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600">
              <Plus size={13} /> Filtre ekle
            </button>
          </div>
        ) : (
          <GroupEditor node={root} onChange={onChange} depth={0} ctx={ctx} />
        )}
        {controlled && !isEmpty && (
          <p className="mt-3 text-[11px] text-gray-400">
            Bu liste sunucuda sayfalanıyor: koşullar sütun hunileriyle aynı süzgece yazılır (her sütun bir kez, hepsi “ve”).
          </p>
        )}
      </div>
    </div>,
    document.body,
  );
}

export { ruleComplete };
